// Port of SuiteModel/LookUp.swift (design-docs/Look Up — design.md): what the project knows about a word in the text.
// The writer selects *Leicester* or *Utterson* and asks; the answer is made of layers, resolved locally and in order —
// the cast, the glossary, research notes titled after it, notes that mention it, the manuscript's scenes that mention
// it — or nothing. `lookUpNames` matches cast and glossary names whole (never by part: empty beats wrong) and
// `resolveLookUp` adds what was found in the texts: on the Mac the index's presence rows and FTS, here the presence rows
// the app works out and a whole-word scan of each document's words (`wholeWordHits`), which stand in for the index.
import type { FragmentKind } from './contract.ts';
import type { StoryEntity } from './model/documents.ts';
import { entityNames, presenceRank, type PresenceLevel, type PresenceRow } from './presence.ts';
import { spellings, type GlossaryEntry } from './story/glossary.ts';
import { graphemes } from './unicode.ts';

/** The words looked up: the selection, trimmed, else the word at the caret. */
export interface LookUpQuery {
  text: string;
  /** `text` folded as names are compared: case, accents and width ignored, spaces collapsed. */
  key: string;
}

/** A document the answer can name: a research note, a study page, a scene. */
export interface LookUpPage { id: string; title: string; kind: FragmentKind }

/** The project as the caller holds it. */
export interface LookUpSources {
  entities: StoryEntity[];
  glossary: GlossaryEntry[];
  /** Research notes — every reference arrangement's documents but the Trash's — in reading order. */
  notes: LookUpPage[];
  /** The manuscript's scenes in reading order. */
  scenes: LookUpPage[];
}

/** What was found in the texts: presence rows for the matched entities, and whole-word matches (fragment → snippet). */
export interface LookUpHits {
  mentions: PresenceRow[];
  snippets: Map<string, string>;
}

export interface LookUpNote { page: LookUpPage; snippet: string | null }
export interface LookUpScene { page: LookUpPage; level: PresenceLevel; snippet: string | null }

export interface LookUpAnswer {
  query: LookUpQuery;
  /** The entity the words name — several when an alias is shared, for the panel to ask which. */
  cast: StoryEntity[];
  /** The glossary entry, when no entity took the words. */
  glossary: GlossaryEntry | null;
  /** Research notes titled after it: the cast's study pages first, then by reading order. */
  pages: LookUpPage[];
  /** Research notes that mention it (not the pages), in reading order. */
  notes: LookUpNote[];
  /** The manuscript's scenes that mention it, in reading order, each at its presence level. */
  scenes: LookUpScene[];
  isEmpty: boolean;
}

const EDGE_PUNCTUATION = /^[\s\p{P}]+|[\s\p{P}]+$/gu;

/** `Series.key`: spaces collapsed; case, accents and width ignored. */
export function lookUpKey(name: string): string {
  return name.split(/\s+/u).filter(Boolean).join(' ').normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().normalize('NFC');
}

/** `LookUp.Query.init`: trimmed of surrounding punctuation, at most 80 characters, one line, with a letter; else null. */
export function lookUpQuery(raw: string): LookUpQuery | null {
  const trimmed = raw.replace(EDGE_PUNCTUATION, '');
  if (trimmed === '' || graphemes(trimmed).length > 80 || /[\r\n\p{Zl}\p{Zp}]/u.test(trimmed) || !/\p{L}/u.test(trimmed)) return null;
  const text = trimmed.split(/\s+/u).filter(Boolean).join(' ');
  return { text, key: lookUpKey(text) };
}

/** `LookUp.names(for:in:)`: the first two layers — the entities whose name or alias is the query, else the glossary
 *  entry whose term or other spelling is. Never by prefix. */
export function lookUpNames(query: LookUpQuery, sources: LookUpSources): { cast: StoryEntity[]; glossary: GlossaryEntry | null } {
  const cast = sources.entities.filter((e) => entityNames(e).some((n) => lookUpKey(n) === query.key));
  if (cast.length) return { cast, glossary: null };
  const entry = sources.glossary.find((e) => spellings(e).some((s) => lookUpKey(s) === query.key)) ?? null;
  return { cast: [], glossary: entry };
}

/** `LookUp.resolve(_:in:hits:)`: the whole answer. */
export function resolveLookUp(query: LookUpQuery, sources: LookUpSources, hits: LookUpHits = { mentions: [], snippets: new Map() }): LookUpAnswer {
  const { cast, glossary } = lookUpNames(query, sources);
  const castIDs = new Set(cast.map((e) => e.id));

  const pages: LookUpPage[] = [];
  const seen = new Set<string>();
  const notesByID = new Map(sources.notes.map((p) => [p.id, p]));
  for (const entity of cast) {
    const study = entity.studyID;
    const page = study !== undefined ? notesByID.get(study) : undefined;
    if (study !== undefined && page && !seen.has(study)) { seen.add(study); pages.push(page); }
  }
  for (const page of sources.notes) {
    if (lookUpKey(page.title) === query.key && !seen.has(page.id)) { seen.add(page.id); pages.push(page); }
  }

  const levels = new Map<string, PresenceLevel>();
  for (const row of hits.mentions) {
    if (!castIDs.has(row.entityID) || row.level === 'absent') continue;
    const have = levels.get(row.fragmentID);
    if (!have || presenceRank(row.level) > presenceRank(have)) levels.set(row.fragmentID, row.level);
  }
  const mentioned = (id: string) => (castIDs.size ? levels.has(id) : hits.snippets.has(id));

  const notes = sources.notes.filter((p) => !seen.has(p.id) && mentioned(p.id)).map((page) => ({ page, snippet: hits.snippets.get(page.id) ?? null }));
  const scenes = sources.scenes.filter((p) => mentioned(p.id)).map((page) => ({ page, level: levels.get(page.id) ?? 'mentioned', snippet: hits.snippets.get(page.id) ?? null }));
  const isEmpty = !cast.length && !glossary && !pages.length && !notes.length && !scenes.length;
  return { query, cast, glossary, pages, notes, scenes, isEmpty };
}

// MARK: Whole words in open text (what the index's FTS does on the Mac)

const WORD_CHARACTER = /^[\p{Alphabetic}\p{N}]/u;
const fold = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().normalize('NFC');

export interface Token { word: string; start: number; end: number }

/** The words of a text as `WordQuery.words` splits them (folded; anything but letters and digits separates), each with
 *  its UTF-16 range in the text. */
export function tokenize(text: string): Token[] {
  const out: Token[] = [];
  let word = '';
  let start = 0;
  let at = 0;
  for (const g of graphemes(text)) {
    const folded = fold(g);
    if (WORD_CHARACTER.test(folded)) {
      if (!word) start = at;
      word += folded;
    } else if (word) {
      out.push({ word, start, end: at });
      word = '';
    }
    at += g.length;
  }
  if (word) out.push({ word, start, end: at });
  return out;
}

/** Where a phrase's words run, whole and in order, in a text's tokens; -1 when they don't. */
export function wholeWordIndex(tokens: readonly Token[], phrase: readonly string[]): number {
  if (!phrase.length) return -1;
  for (let i = 0; i + phrase.length <= tokens.length; i++) {
    if (phrase.every((w, j) => tokens[i + j].word === w)) return i;
  }
  return -1;
}

const CONTEXT = 12;

/** `SearchIndex.wholeWordMatches` for one text: the snippet around the first whole-word match (the hit in `[ ]`, about
 *  twelve words either side, `…` where it's cut), or null when the words aren't there. */
export function wholeWordSnippet(text: string, words: string, tokens: readonly Token[] = tokenize(text)): string | null {
  const phrase = tokenize(words).map((t) => t.word);
  const at = wholeWordIndex(tokens, phrase);
  if (at < 0) return null;
  const first = Math.max(0, at - CONTEXT);
  const last = Math.min(tokens.length - 1, at + phrase.length - 1 + CONTEXT);
  const hitStart = tokens[at].start, hitEnd = tokens[at + phrase.length - 1].end;
  const before = text.slice(tokens[first].start, hitStart);
  const hit = text.slice(hitStart, hitEnd);
  const after = text.slice(hitEnd, tokens[last].end);
  return (first > 0 ? '…' : '') + before + '[' + hit + ']' + after + (last < tokens.length - 1 ? '…' : '');
}

/** A document the app can scan: its body and notes as plain text (the index's `body` and `notes` columns). */
export interface LookUpText { id: string; body: string; notes: string }

/** `LookUp.hits(for:cast:index:projectID:)` without an index: the presence rows for the entities found (and their
 *  names' snippets), else the query's words whole. `tokens` may cache each text's tokens between calls. */
export function lookUpHits(query: LookUpQuery, cast: StoryEntity[], texts: Iterable<LookUpText>, presence: readonly PresenceRow[],
  tokens: (t: LookUpText, column: 'body' | 'notes') => Token[] = (t, c) => tokenize(c === 'body' ? t.body : t.notes)): LookUpHits {
  const hits: LookUpHits = { mentions: [], snippets: new Map() };
  const names = cast.length ? [...new Set(cast.flatMap((e) => entityNames(e)))] : [query.text];
  if (cast.length) {
    const ids = new Set(cast.map((e) => e.id));
    hits.mentions = presence.filter((r) => ids.has(r.entityID));
  }
  for (const text of texts) {
    for (const name of names) {
      const found = wholeWordSnippet(text.body, name, tokens(text, 'body')) ?? wholeWordSnippet(text.notes, name, tokens(text, 'notes'));
      if (found !== null) { hits.snippets.set(text.id, found); break; }
    }
  }
  return hits;
}
